// bindingOps — pure helpers over the lookup elements' `bindingList`
// (`dataSource` and `companyField`: a form field wired to a column of the
// picked source). A row keys on the bound field's WIRE id — `elementGlobalId`,
// or `uniqueIdToReplace` while the field has never been saved — and bindings
// are form-wide: a host on one page may bind a field on another, so a prune
// has to walk every page even though the deletion happened on one.

import type { BuilderField, BuilderPage } from './builderModel';
import type {
  DataSourceBinding,
  ElementProperties,
  LibraryElementType,
} from './formElementDefaults';

const BINDING_HOSTS: ReadonlySet<LibraryElementType> = new Set<LibraryElementType>([
  'dataSource',
  'companyField',
]);

/** Drop every `bindingList` row that points at one of `removed` (the rows a
 *  field / section / page deletion takes off the canvas). Reference-stable:
 *  untouched fields and pages come back as the same objects, and the input
 *  array itself when no row was dropped. */
export const removeBindingsToFields = (
  pages: BuilderPage[],
  removed: BuilderField[],
): BuilderPage[] => {
  const gone = new Set<string>();
  for (const f of removed) {
    const id = f.elementGlobalId ?? f.uniqueIdToReplace;
    if (id) gone.add(id);
  }
  if (gone.size === 0) return pages;
  let changed = false;
  const next = pages.map((page) => {
    let pageChanged = false;
    const fields = page.fields.map((field) => {
      if (!BINDING_HOSTS.has(field.elementType)) return field;
      const basic = field.properties.basicProperties as { bindingList?: DataSourceBinding[] };
      const list = basic.bindingList ?? [];
      const kept = list.filter((b) => !gone.has(b.fieldId));
      if (kept.length === list.length) return field;
      pageChanged = true;
      return {
        ...field,
        properties: {
          ...field.properties,
          basicProperties: { ...basic, bindingList: kept },
        } as ElementProperties,
      };
    });
    if (!pageChanged) return page;
    changed = true;
    return { ...page, fields };
  });
  return changed ? next : pages;
};
